import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalSize, ModalVariant } from '@/features/configuration-management/location/table/Column';
import { TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import { DeleteMainPageHostPayload } from '@/services/mainPage/storeList/hostList/hostList.mutations';
import { HostList } from '@/services/mainPage/storeList/hostList/hostList.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';

export function getColumns(
  t: (key: string, options?: Record<string, string>) => string,
  openConfirmation: (data: {
    title: string;
    description?: string | undefined;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  deleteHost: UseMutateAsyncFunction<
    {
      message: string;
    },
    StandardizedApiError,
    DeleteMainPageHostPayload,
    unknown
  >,
): ColumnDef<HostList>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'hostName',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.hostName')}
        />
      ),
      cell: ({ row }) => <div className="font-medium text-caption">{row.original.hostName}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.requestedDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption">
          {row.original.createdDate ? formatDate(row.original.createdDate) : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.updatedDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption">
          {row.original.updatedDate ? formatDate(row.original.updatedDate) : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => (
        <Can rule={{ allOf: [PERMS.recommendation.mainPage.host.delete] }}>
          <div className="cursor-pointer">
            <TrashIcon
              className="size-4 text-destructive-text"
              onClick={() =>
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: t('common.page.modal.deleteStaffMessage', { staffName: row.original.hostName }),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  size: 'lg',
                  onAction: async () => {
                    await deleteHost({ recommendationId: row.original.recommendationId, hostId: row.original.hostId });
                  },
                  onCancel: () => {},
                  variant: 'destructive',
                })
              }
            />
          </div>
        </Can>
      ),
    },
  ];
}
